'use client';

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  detectRegion,
  saveConsent,
  getConsent,
  logConsentAction,
  type ConsentRegion,
} from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

export default function CookieBanner() {
  const [region, setRegion] = useState<ConsentRegion>('none');
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    const detected = detectRegion();
    setRegion(detected);

    const existing = getConsent();
    if (existing) {
      setHasConsent(true);
    } else if (detected !== 'none') {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    saveConsent(
      {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true,
      },
      region
    );
    logConsentAction('accept_all', { region });
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    saveConsent(
      {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
      },
      region
    );
    logConsentAction('reject_all', { region });
    setShowBanner(false);
  };

  const handleCCPADismiss = () => {
    // CCPA: analytics true, marketing false, preferences true
    saveConsent(
      {
        necessary: true,
        analytics: true,
        marketing: false,
        preferences: true,
      },
      region
    );
    logConsentAction('ccpa_dismiss', { region });
    setShowBanner(false);
  };

  if (!showBanner || region === 'none') return null;

  return (
    <>
      {region === 'gdpr' && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900 border-t border-slate-700 p-6 shadow-2xl">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start justify-between gap-6 mb-4">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-white mb-2">
                  Cookie Consent
                </h2>
                <p className="text-sm text-slate-300 mb-3">
                  We use cookies to enhance your experience, analyze site traffic, and enable personalized features.
                  By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or
                  learn more in our{' '}
                  <a href="/privacy" className="text-blue-400 hover:text-blue-300 underline">
                    Privacy Policy
                  </a>
                  {' '}and{' '}
                  <a href="/cookies" className="text-blue-400 hover:text-blue-300 underline">
                    Cookie Policy
                  </a>
                  .
                </p>
              </div>
              <button
                onClick={() => setShowBanner(false)}
                className="text-slate-400 hover:text-white transition-colors flex-shrink-0"
                aria-label="Close banner"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex flex-wrap gap-3 justify-start">
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={handleRejectAll}
                className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors"
              >
                Reject All
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors"
              >
                Manage Preferences
              </button>
            </div>
          </div>
        </div>
      )}

      {region === 'ccpa' && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900 border-t border-slate-700 p-6 shadow-2xl">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <p className="text-sm text-slate-300">
                  We respect your privacy. Under the California Consumer Privacy Act (CCPA), you have the right to
                  know what personal information we collect and how it is used. Learn more in our{' '}
                  <a href="/privacy#do-not-sell" className="text-blue-400 hover:text-blue-300 underline">
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
              <button
                onClick={handleCCPADismiss}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex-shrink-0"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <ConsentPreferenceCenter
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        region={region}
      />
    </>
  );
}
